/* eslint-disable react-refresh/only-export-components --
 * `useTemCamposPersonalizados` compartilha o MESMO `queryKey` do bloco — é isso
 * que faz a pergunta "tem campo?" não custar requisição extra. Movê-lo para
 * outro arquivo separaria os dois pedaços de uma decisão só e convidaria alguém
 * a inventar uma segunda chave de cache. */
import { useQueryClient } from '@tanstack/react-query';

import { HelpTip } from '@/components/ui';

import { DynamicForm } from './DynamicForm';
import { useFormSchema } from './useFormSchema';

/**
 * CamposPersonalizadosBloco — a MESMA seção de campos personalizados nas telas
 * internas (K-02, 2026-07-30).
 *
 * Por que existe um wrapper em vez de cada tela montar o `DynamicForm` na mão:
 * a rodada K nasceu porque a feature tinha dois renderizadores que evoluíram
 * separados (ver `docs/architecture/CAMPOS-PERSONALIZADOS-DECISAO.md`). Se cada
 * uma das telas escrever seu próprio cabeçalho, seu próprio "?" e sua própria
 * regra de quando esconder, a duplicidade volta pela porta dos fundos — só que
 * na camada de cima. Aqui é UM lugar: título, ajuda e a decisão de sumir
 * quando não há campo configurado.
 *
 * Montado hoje em três telas (a quarta, o formulário público, é caso à parte —
 * lá não há sessão e o schema vai por `schemaExterno`):
 *   - ficha do LEAD          → `entidade="lead"`       (`src/pages/comercial/LeadsPage.tsx`)
 *   - ficha do CLIENTE       → `entidade="cliente"`    (`src/pages/admin/ClientDetailPage.tsx`)
 *   - OS / Manutenção        → `entidade="manutencao"` (`src/pages/menu/manutencao/OsDetailDialog.tsx`)
 *
 * PERMISSÃO: nada de gate novo. Criar campo é admin (as rotas
 * `/admin/custom-fields` já são `requireAuth + requireAdmin`); preencher é de
 * quem já entrou na tela da entidade — o `/forms/schema` ainda filtra campo a
 * campo por perfil (`visivel_para_perfis` / `editavel_para_perfis`), então um
 * campo restrito não aparece nem editável nem legível pra quem não pode.
 */
export interface CamposPersonalizadosBlocoProps {
  /** Entidade do EAV: 'lead' | 'cliente' | 'manutencao' | … */
  entidade: string;
  /** ID do registro ABERTO. Sem ele não há onde salvar — o bloco não monta. */
  entidadeId: string | null | undefined;
  /** Título da seção. Default "Campos personalizados". */
  titulo?: string;
  /** Texto do "?" — cada tela explica o que aquele bloco significa ali. */
  ajuda: string;
  /** `data-testid` do container. */
  testId?: string;
  /** Chamado depois de salvar (pra tela recarregar o que depender do valor). */
  onSalvo?: () => void;
  className?: string;
}

/**
 * Existe campo personalizado configurado (e visível pra quem está olhando)
 * nesta entidade?
 *
 * Serve pra tela decidir sobre a MOLDURA — o `<Card>` da ficha do cliente, por
 * exemplo. O bloco sozinho já some quando não há campo, mas o card em volta
 * dele não: sem esta pergunta, a ficha ganharia um retângulo vazio.
 *
 * Compartilha o `queryKey` do bloco, então não custa uma requisição a mais.
 */
export function useTemCamposPersonalizados(
  entidade: string,
  entidadeId: string | null | undefined,
): boolean {
  const { data: schema } = useFormSchema({
    entidade,
    entidadeId,
    enabled: Boolean(entidadeId),
  });
  // `?.` nos DOIS níveis: guardar só o `schema` não basta. Uma resposta sem a
  // chave `campos` — API antiga, erro devolvido com corpo diferente, mock de
  // teste que não conhece este endpoint — estourava aqui e derrubava a TELA
  // INTEIRA em volta (o modal da OS, a ficha do cliente). Uma seção opcional
  // não pode ter esse poder.
  return Boolean(entidadeId) && (schema?.campos?.length ?? 0) > 0;
}

export function CamposPersonalizadosBloco({
  entidade,
  entidadeId,
  titulo = 'Campos personalizados',
  ajuda,
  testId,
  onSalvo,
  className,
}: CamposPersonalizadosBlocoProps) {
  /**
   * O mesmo `queryKey` que o `DynamicForm` usa lá dentro — o React Query
   * responde as duas chamadas com UMA requisição. Precisamos do schema aqui em
   * cima só pra decidir se a seção existe: sem isso, uma entidade sem nenhum
   * campo configurado mostraria um título solto com nada embaixo (o
   * `DynamicForm` devolve `null` nesse caso).
   */
  const { data: schema, isLoading } = useFormSchema({
    entidade,
    entidadeId,
    enabled: Boolean(entidadeId),
  });
  const qc = useQueryClient();

  /**
   * Depois de salvar, o schema em cache está velho — ele carrega o `valor` de
   * cada campo, e o `staleTime` do `useFormSchema` é de 30s. Sem invalidar,
   * quem salva, fecha a ficha e reabre dentro desse meio minuto vê o valor
   * ANTIGO de volta e conclui que o salvamento não pegou.
   */
  function aoSalvar() {
    void qc.invalidateQueries({ queryKey: ['form-schema', entidade, entidadeId] });
    onSalvo?.();
  }

  if (!entidadeId) return null;
  if (isLoading) return null;
  if (!schema?.campos?.length) return null;

  return (
    <div className={className} data-testid={testId}>
      <div className="mb-3 flex items-center gap-1.5">
        <h3 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">{titulo}</h3>
        <HelpTip texto={ajuda} />
      </div>
      <DynamicForm entidade={entidade} entidadeId={entidadeId} onSubmitted={aoSalvar} />
    </div>
  );
}
